window.graphqlFetch = async (query, variables = {}) => {
  const response = await fetch('/api/2024-10/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.STOREFRONT_API_TOKEN
    },
    body: JSON.stringify({ query, variables })
  });

  const json = await response.json();

  if (json.errors) {
    console.error('GraphQL errors:', json.errors);
    throw new Error('GraphQL request failed');
  }

  return json.data;
};

class BlogArticles extends HTMLElement {
  constructor() {
    super();

    this.container = this.querySelector('#blog-articles-search');
    this.searchInput = this.querySelector('#article-search-input');
    this.searchButton = this.querySelector('#article-search-submit');
    this.clearButton = this.querySelector('#article-search-clear');
    this.sortInput = this.querySelector('#article-sort');
    this.loadMoreButton = this.querySelector('#load-more-articles');
    this.numTotals = this.querySelectorAll('.result-count .num-total');
    this.numLoadeds = this.querySelectorAll('.result-count .num-loaded');

    this.blogHandle = this.dataset.blogHandle || 'news';
    this.pageSize = Number(this.dataset.pageSize) || 10;

    this.endCursor = null;
    this.hasNextPage = true;
    this.loadedItems = Number(this.dataset.articlesLoaded) || 0;
    this.totalItems = Number(this.dataset.articlesCount) || 0;
    this.paginationContainer = this.querySelector('.pagination-wrapper');
    this.progressBar = this.querySelector('.progress-bar');
    this.resultCount = this.querySelector('.result-count');

    this.initialPrefetchDone = true;
    this.isSearchLoad = false;

    const path = window.location.pathname;
    const tagPart = path.split('tagged/')[1];
    this.selectedTags = tagPart ? tagPart.split('+') : [];
  }

  connectedCallback() {
    this.searchButton?.addEventListener('click', () => {
      this.isSearchLoad = true;
      this.resetAndSearch();
    });

    this.searchInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.isSearchLoad = true;
        this.resetAndSearch();
      }
    });

    this.searchInput?.addEventListener('input', (e) => {
      this.toggleClearButton(false)
    });

    this.clearButton?.addEventListener('click', () => {
      this.searchInput.value = '';

      this.isSearchLoad = false;
      this.initialPrefetchDone = false;
      this.endCursor = null;
      this.hasNextPage = true;
      this.toggleClearButton(false);

      this.container.innerHTML = '';
      this.loadMore({ reset: true });
    });


    this.sortInput?.addEventListener('change', () => {
      this.isSearchLoad = false;
      this.resetAndSearch();
    });

    this.loadMoreButton?.addEventListener('click', () => {
      this.isSearchLoad = false;
      this.loadMore();
    });
  }


  resetAndSearch() {
    this.initialPrefetchDone = false;
    this.endCursor = null;
    this.hasNextPage = true;

    if (this.loadMoreButton) {
      this.loadMoreButton.style.display = 'flex';
    }

    this.container.innerHTML = '';
    this.loadMore({ reset: true });
  }

  async loadMore(params = {}) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.setLoading(true);

    const searchTerm = this.searchInput?.value.trim() || '';
    const tags = this.selectedTags || [];

    let finalQuery = searchTerm;

    if (!this.isSearchLoad && tags.length) {
      const tagsQuery = tags.map(tag => `tag:${tag}`).join(' OR ');
      finalQuery = searchTerm
        ? `${searchTerm} AND (${tagsQuery})`
        : tagsQuery;
    }

    const sortValue = (this.sortInput ? this.sortInput.value : 'published_at-desc').split('-');

    const sortMap = {
      published_at: 'PUBLISHED_AT',
      title: 'TITLE',
      relevance: 'RELEVANCE'
    };

    const sortType = sortMap[sortValue[0]] || 'PUBLISHED_AT';
    const isReverse = sortValue[1] === 'desc';
    let pageSize = this.pageSize

    if (this.initialPrefetchDone === true) {
      pageSize = pageSize * 2
    }
    
    const query = `
      query BlogArticles($search: String!, $after: String) {
        blog(handle: "${this.blogHandle}") {
          articles(
            first: ${pageSize}
            query: $search
            sortKey: ${sortType}
            reverse: ${isReverse}
            after: $after
          ) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                handle
              }
            }
          }
        }
      }
    `;

    try {
      const data = await window.graphqlFetch(query, {
        search: finalQuery,
        after: this.endCursor
      });

      const { edges, pageInfo } = data.blog.articles;

      let newArticles = edges

      if (this.initialPrefetchDone === true) {
        newArticles = newArticles.slice(this.pageSize);
        this.initialPrefetchDone = false
      }

      this.endCursor = pageInfo.endCursor;
      this.hasNextPage = pageInfo.hasNextPage;
        
      await this.render(newArticles, params.reset);

      if (this.isSearchLoad) {
        this.toggleClearButton();
      }

      if (this.loadMoreButton) {
        this.loadMoreButton.style.display = this.hasNextPage ? 'flex' : 'none';
      }
    } catch (err) {
      console.error('BlogArticles error:', err);
    } finally {
      this.isLoading = false;
      this.setLoading(false);
    }
  }

  toggleClearButton(showClear = true) {
    if (showClear) {
      this.clearButton.classList.remove("hidden")
      this.searchButton.classList.add("hidden")
    } else {
      this.clearButton.classList.add("hidden")
      this.searchButton.classList.remove("hidden")
    }
  }

  async render(articles, reset = false) {
    const hasRealItems = this.container.querySelector('.blog-listing__article');
    const noResultsFound = !articles.length && !hasRealItems;

    if (noResultsFound) {
      this.container.innerHTML = '<p>No results found.</p>';
      this.updateUI(true, reset);
    } else {
      if (reset) {
        this.container.innerHTML = '';
      }
      let renderedCount = 0;

      articles.forEach(({ node }) => {
        const url = `/blogs/${this.blogHandle}/${node.handle}?view=card`;

        fetch(url)
          .then(res => res.text())
          .then(html => {
            this.container.insertAdjacentHTML(
              'beforeend',
              `<div class="blog-listing__article">${html}</div>`
            );

            renderedCount++;

            if (renderedCount === articles.length) {
              this.updateUI(false, reset);
            }
          });
      });
    }
  }

  updateUI(noResultsFound, reset) {
    const loadedCount = noResultsFound ? 0 : this.container.children.length;

    this.numLoadeds.forEach(numLoaded => {
      numLoaded.textContent = loadedCount;
    });

    this.loadedItems = loadedCount;

    if (reset) {
      if (noResultsFound) {
        this.totalItems = 0;
        this.numTotals.forEach(numTotal => {
          numTotal.textContent = 0;
        });
        this.paginationContainer.classList.add('hidden')
      }
    }

    this.toggleTotalCount()
    this.updateProgress();
  }

  toggleTotalCount() {
    if (this.isSearchLoad) {
      this.numTotals.forEach(numTotal => {
        numTotal.classList.add('hidden');

        const prev = numTotal.previousElementSibling;
        if (prev) prev.classList.add('hidden');
      });
    } else {
      this.numTotals.forEach(numTotal => {
        numTotal.classList.remove('hidden');

        const prev = numTotal.previousElementSibling;
        if (prev) prev.classList.remove('hidden');
      });
    }
  }

  updateProgress() {
    if (!this.totalItems) {
      this.progressBar.style.setProperty('--progress', '0%');
      return;
    }

    const progress = (this.loadedItems / this.totalItems) * 100;
    this.progressBar.style.setProperty('--progress', `${progress}%`);
  }

  setLoading(isLoading) {
    this.classList.toggle('loading', isLoading);
  }
}

customElements.define('blog-articles', BlogArticles);
